import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { FaPlus, FaFileExport, FaFileImport, FaDownload, FaSearch, FaFilter } from 'react-icons/fa';
import Swal from 'sweetalert2';

function TableToolbar({ 
  title, 
  subtitle, 
  onAdd, 
  onExport, 
  onImport, 
  onDownloadSample,
  onSearch,
  onFilterChange,
  filterOptions = [], // [{ name, value, options: [{label, value}] }]
  loading = false,
  // eslint-disable-next-line no-unused-vars
  permissions = []
}) {
  const fileInputRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const csv = event.target.result;
      try {
        await onImport(csv);
        Swal.fire('Success', 'Data imported successfully', 'success');
      } catch (err) {
        Swal.fire('Error', err.message, 'error');
      }
    };
    reader.readAsText(file);
    e.target.value = null; // reset for next upload
  };

  return (
    <div className="eduss-table-toolbar premium-card">
      <div className="toolbar-top">
        <div className="toolbar-info">
          <h2>{title}</h2>
          {subtitle && <p className="toolbar-subtitle">{subtitle}</p>}
        </div>
        <div className="toolbar-main-actions">
          {onAdd && (
            <button className="eduss-global-table-btn add modern" onClick={onAdd} disabled={loading}>
              <FaPlus /> Add New
            </button>
          )}
          <div className="action-dropdown-group">
            {onExport && (
              <button className="eduss-global-table-btn export modern" onClick={onExport} title="Export to CSV">
                <FaFileExport /> Export
              </button>
            )}
            {onImport && (
              <>
                <button className="eduss-global-table-btn import modern" onClick={() => fileInputRef.current.click()} title="Import from CSV">
                  <FaFileImport /> Import
                </button>
                <input type="file" ref={fileInputRef} onChange={handleFileChange} accept=".csv" style={{ display: 'none' }} />
              </>
            )}
            {onDownloadSample && (
              <button className="eduss-global-table-btn sample modern" onClick={onDownloadSample} title="Download Sample CSV">
                <FaDownload /> Sample
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="toolbar-bottom">
        <div className="search-wrapper">
          <FaSearch className="search-icon" />
          <input 
            type="text" 
            placeholder={`Search ${title ? title.toLowerCase() : ''}...`} 
            onChange={(e) => onSearch(e.target.value)} 
            className="modern-filter-input search-field"
          />
        </div>
        <div className="filters-wrapper">
          {filterOptions.map((f, i) => (
            <div key={i} className="filter-item">
              <FaFilter className="filter-icon" />
              <select 
                value={f.value} 
                onChange={(e) => onFilterChange(f.name, e.target.value)}
                className="modern-filter-input"
              >
                <option value="">{`All ${f.name.charAt(0).toUpperCase() + f.name.slice(1)}`}</option>
                {f.options.map((opt, oi) => (
                  <option key={oi} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .eduss-table-toolbar {
          padding: 14px 16px;
          margin-bottom: 12px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .toolbar-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .toolbar-info h2 {
          margin: 0;
          font-size: 1.08rem;
          font-weight: 700;
          color: #2d3436;
        }
        .toolbar-info p {
          margin: 2px 0 0 0;
          color: #636e72;
          font-size: 0.76rem;
        }
        .toolbar-subtitle {
          display: none;
        }
        .toolbar-main-actions {
          display: flex;
          gap: 8px;
        }
        .action-dropdown-group {
          display: flex;
          gap: 6px;
        }
        .toolbar-bottom {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          border-top: 1px solid #f1f1f1;
          padding-top: 10px;
        }
        .search-wrapper {
          position: relative;
          flex: 1;
        }
        .search-field {
          width: 100%;
          padding-left: 34px !important;
        }
        .search-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #aaa;
          font-size: 0.78rem;
        }
        .filters-wrapper {
          display: flex;
          gap: 8px;
        }
        .eduss-global-table-btn.modern {
          border-radius: 7px;
          padding: 7px 11px;
          min-height: 32px;
          font-size: 0.76rem;
          font-weight: 600;
          transition: all 0.2s;
        }
        .eduss-global-table-btn.import { background: #6c5ce7; color: white; }
        .eduss-global-table-btn.sample { background: #fab1a0; color: #d63031; }
      `}} />
    </div>
  );
}

TableToolbar.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  onAdd: PropTypes.func,
  onExport: PropTypes.func,
  onImport: PropTypes.func,
  onDownloadSample: PropTypes.func,
  onSearch: PropTypes.func,
  onFilterChange: PropTypes.func,
  filterOptions: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string
    }))
  })),
  loading: PropTypes.bool,
  permissions: PropTypes.array
};

export default TableToolbar;
